import { useMemo, useState } from "react";
import type { ReactNode } from "react";

export interface Column<T> {
  key: string;
  label: string;
  /** Custom cell renderer; defaults to rendering row[key] as text. */
  render?: (row: T) => ReactNode;
  /** Value used for sorting; defaults to row[key] when it is a primitive. */
  sortValue?: (row: T) => string | number | null;
  /** CSS width, e.g. "120px" or "12%". */
  width?: string;
  /** Right-align the column (numbers). */
  align?: "left" | "right";
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  onRowClick?: (row: T) => void;
  /** Stable row key; defaults to row.id when present, else the index. */
  rowKey?: (row: T, index: number) => string | number;
  defaultSortKey?: string;
  defaultSortDir?: "asc" | "desc";
  emptyMessage?: string;
  rowClassName?: (row: T) => string | undefined;
}

function defaultSortValue<T>(row: T, key: string): string | number | null {
  const v = (row as Record<string, unknown>)[key];
  if (v == null) return null;
  if (typeof v === "number" || typeof v === "string") return v;
  if (typeof v === "boolean") return v ? 1 : 0;
  return String(v);
}

function defaultRowKey<T>(row: T, index: number): string | number {
  const id = (row as Record<string, unknown>)["id"];
  if (typeof id === "string" || typeof id === "number") return id;
  return index;
}

function defaultCell<T>(row: T, key: string): ReactNode {
  const v = (row as Record<string, unknown>)[key];
  if (v == null) return <span className="muted">—</span>;
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "number" || typeof v === "string") return v;
  return String(v);
}

/**
 * Generic sortable table with a sticky header. Click a header to sort; click
 * again to flip direction. Nulls always sort last.
 */
export function DataTable<T>({
  columns,
  rows,
  onRowClick,
  rowKey = defaultRowKey,
  defaultSortKey,
  defaultSortDir = "asc",
  emptyMessage = "Nothing here yet",
  rowClassName,
}: DataTableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(defaultSortKey ?? null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">(defaultSortDir);

  const sorted = useMemo(() => {
    if (!sortKey) return rows;
    const col = columns.find((c) => c.key === sortKey);
    if (!col) return rows;
    const getVal = col.sortValue ?? ((row: T) => defaultSortValue(row, sortKey));
    const dir = sortDir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = getVal(a);
      const bv = getVal(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1; // nulls last regardless of direction
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return (
        String(av).localeCompare(String(bv), undefined, { sensitivity: "base", numeric: true }) * dir
      );
    });
  }, [rows, columns, sortKey, sortDir]);

  function toggleSort(key: string) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                style={col.width ? { width: col.width } : undefined}
                className={col.align === "right" ? "right" : undefined}
                aria-sort={
                  sortKey === col.key ? (sortDir === "asc" ? "ascending" : "descending") : undefined
                }
              >
                <button type="button" className="th-sort" onClick={() => toggleSort(col.key)}>
                  {col.label}
                  {sortKey === col.key && (
                    <span className="sort-indicator">{sortDir === "asc" ? "▲" : "▼"}</span>
                  )}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && (
            <tr>
              <td className="empty-cell" colSpan={columns.length}>
                {emptyMessage}
              </td>
            </tr>
          )}
          {sorted.map((row, i) => {
            const extra = rowClassName ? rowClassName(row) : undefined;
            const cls = [onRowClick ? "clickable" : "", extra ?? ""].join(" ").trim();
            return (
              <tr
                key={rowKey(row, i)}
                className={cls || undefined}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((col) => (
                  <td key={col.key} className={col.align === "right" ? "right" : undefined}>
                    {col.render ? col.render(row) : defaultCell(row, col.key)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default DataTable;
